// src/pages/ProductInfo.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/navBar';
import Footer from '../components/footer';
import './productProfile.css'; // Custom CSS if needed
import Recommended from '../components/product_profile/recommend';

const ProductInfo = () => {
  const location = useLocation();
  const { product } = location.state || {};
  const { id } = useParams();
  const navigate = useNavigate();

  // Debug: Log product.images to verify structure
  useEffect(() => {
    console.log("Product images:", product?.images);
  }, [product]);

  // Set main image to the first element of product.images.light if available.
  const [mainImage, setMainImage] = useState(
    product && product.images && Array.isArray(product.images.light) && product.images.light.length > 0
      ? product.images.light[0]
      : ''
  );

  // Accordion state: which panel is open. Default panel1 open.
  const [openPanel, setOpenPanel] = useState('panel1');

  // Toggle function for accordion panels.
  const togglePanel = (panelId) => {
    setOpenPanel((prev) => (prev === panelId ? null : panelId));
  };

  // Function to copy the product id to clipboard.
  const copyItemId = () => {
    if (product && product.id) {
      navigator.clipboard.writeText(product.id)
        .then(() => {
          alert("Item ID copied to clipboard!");
        })
        .catch((err) => {
          console.error("Failed to copy: ", err);
        });
    }
  };

  // Function to handle Buy Now click.
  const handleBuyNow = () => {
    // Redirect to Facebook for now.
    window.location.href = "https://www.facebook.com/nopeetph";
  };

  // Fallback if no product data is available.
  if (!product) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center">
        <Header />
        <div className="p-8 text-center">
          <h2 className="text-2xl font-bold">Product Not Found</h2>
          <p className="mt-4">
            No product information is available. Please go back and select a product.
          </p>
          <Link to="/" className="mt-4 inline-block text-blue-500 underline">
            Return Home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Use the entire product.images.light array as the thumbnail gallery.
  const galleryImages =
    product.images && Array.isArray(product.images.light)
      ? product.images.light
      : [];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <section className="py-8 product-info-section bg-white md:py-8 dark:bg-gray-900 antialiased">
        <div className="max-w-screen-xl px-4 mx-auto 2xl:px-0">
          <div className="lg:grid lg:grid-cols-2 lg:gap-8 xl:gap-16">
            {/* Product Images with Fixed-Size Container and Thumbnail Gallery */}
            <div className="shrink-0 lg:max-w-lg mx-auto">
              {/* Fixed size container for the main image */}
              <div className="relative w-[400px] h-[660px] md:h-[600px]">
                <img
                  className="absolute inset-0 w-full h-full object-cover"
                  src={mainImage}
                  alt={product.title}
                  onError={(e) => {
                    // Fallback image if main image fails to load.
                    e.target.src = 'https://via.placeholder.com/600x400?text=No+Image';
                  }}
                />
              </div>
              {galleryImages.length > 0 && (
                <div className="mt-4 flex space-x-4">
                  {galleryImages.map((img, idx) => (
                    <img
                      key={idx}
                      src={img}
                      alt={`Thumbnail ${idx + 1}`}
                      className={`w-20 h-20 md:w-16 md:h-16 object-cover cursor-pointer border transition-opacity duration-300 opacity-75 hover:opacity-100 ${
                        mainImage === img ? 'border-primary-700' : 'border-gray-200'
                      }`}
                      onClick={() => setMainImage(img)}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/150x150?text=No+Image';
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Product Details */}
            <div className="mt-6 sm:mt-8 lg:mt-0">
              <h1 className="text-xl font-semibold text-gray-900 sm:text-2xl dark:text-white">
                {product.title}
              </h1>
              <div className="mt-4 sm:items-center sm:gap-4 sm:flex">
                <p className="text-xl font-normal text-gray-900 dark:text-white">
                  ${product.price}
                </p>
              </div>
              <div className="mt-6 sm:gap-4 sm:items-center sm:flex">
                {/* Copy Item ID Button */}
                <button
                  type="button"
                  onClick={copyItemId}
                  className="flex items-center justify-center py-2.5 px-5 text-sm font-medium text-gray-900 focus:outline-none bg-white border border-gray-200 hover:bg-gray-100 focus:z-10 focus:ring-4 focus:ring-yellow-50 dark:focus:ring-yellow-50 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-5 h-5 -ml-2 mr-2"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M16 1H4a2 2 0 00-2 2v14h2V3h12V1z" />
                    <path d="M21 5H8a2 2 0 00-2 2v14a2 2 0 002 2h13a2 2 0 002-2V7a2 2 0 00-2-2zm0 16H8V7h13v14z" />
                  </svg>
                  Copy Item ID
                </button>
                {/* Buy Now Button */}
                <button
                  type="button"
                  onClick={handleBuyNow}
                  className="text-white mt-4 sm:mt-0 primary-color hover:bg-yellow-700 font-medium text-sm px-5 py-2.5 focus:outline-none flex items-center justify-center"
                >
                  <svg
                    className="w-5 h-5 -ml-2 mr-2"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4h1.5L8 16m0 0h8m-8 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm8 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm.75-3H7.5M11 7H6.312M17 4v6m-3-3h6" />
                  </svg>
                  Buy Now
                </button>
              </div>
              {/* Guideline Instructions */}
              <div className="mt-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  To proceed with your purchase, please copy the Item ID using the "Copy Item ID" button, then click "Buy Now" and send us a message.
                </p>
              </div>

              {/* Accordion for Product Details */}
              <div className="mt-16">
                {/* Panel 1: Description */}
                <h2 id="accordion-panel-1">
                  <button
                    type="button"
                    onClick={() => togglePanel('panel1')}
                    className="flex items-center justify-between w-full py-5 font-medium text-gray-500 dark:text-gray-400"
                    aria-expanded={openPanel === 'panel1' ? "true" : "false"}
                    aria-controls="panel-1-content"
                  >
                    <span className="font-semibold">Description</span>
                    <svg
                      className={`w-5 h-5 shrink-0 transition-transform ${openPanel === 'panel1' ? "rotate-180" : ""}`}
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 9-7 7-7-7" />
                    </svg>
                  </button>
                </h2>
                <div
                  id="accordion-panel-1"
                  className={openPanel === 'panel1' ? "block border-b border-gray-200 dark:border-gray-700" : "hidden"}
                  aria-labelledby="accordion-panel-1"
                >
                  <div className="py-5">
                    <p className="text-gray-500 dark:text-gray-400">
                      {product.description || "No description available."}
                    </p>
                  </div>
                </div>

                {/* Panel 2: Details */}
                <h2 id="accordion-panel-2">
                  <button
                    type="button"
                    onClick={() => togglePanel('panel2')}
                    className="flex items-center justify-between w-full py-5 font-medium text-gray-500 dark:text-gray-400"
                    aria-expanded={openPanel === 'panel2' ? "true" : "false"}
                    aria-controls="panel-2-content"
                  >
                    <span className="font-semibold">Details</span>
                    <svg
                      className={`w-5 h-5 shrink-0 transition-transform ${openPanel === 'panel2' ? "rotate-180" : ""}`}
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 9-7 7-7-7" />
                    </svg>
                  </button>
                </h2>
                <div
                  id="accordion-panel-2"
                  className={openPanel === 'panel2' ? "block border-b border-gray-200 dark:border-gray-700" : "hidden"}
                  aria-labelledby="accordion-panel-2"
                >
                  <div className="py-5">
                    <p className="text-gray-500 dark:text-gray-400">
                      {product.details || "No details available."}
                    </p>
                  </div>
                </div>
              </div>
              {/* End of Accordion */}
            </div>
          </div>
        </div>
      </section>
      <Recommended />
      <Footer />
    </div>
  );
};

export default ProductInfo;
